import ExcelJS from 'exceljs';
import {
    ArrowLeft,
    Boxes,
    Download,
    Plus,
    RotateCw
} from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { NotificationSound } from '../CommonComponents/CommonNotification';
import { ApiCall, DropDownData, convertToNumber, deleteData, getDateAndTime, handleDateChange, sanitizeInput } from '../CommonComponents/CustomHooks';
import { initializeFilterData } from '../CommonComponents/EnvConfig';
import { AutoCompleteBoxNew, DateBox, FormButton, InputBox, SelectInputBox, TextareaBox } from '../CommonComponents/InputBox';
import { Table } from '../CommonComponents/Table';
import { setSubLoading } from "../../Redux/Services/AuthSlice";
import { handleDownloadExcel } from '../CommonComponents/ExportData';
import { PickupTrackingCard } from './PickupTracking';
import { showConfirmDialog } from '../CommonComponents/ConfirmModal';


// ── Initial Mock Dataset (Exact match with reference PDF) ────────────────────

export const PickupRequests = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const inputrefs = useRef({});

    // Mode: 'list' or 'create'
    const [viewMode, setViewMode] = useState('list');
    let GetVehicleType = DropDownData("GetVehicleType");
    // Search Filter States for Filter Card
    const initialFilterState = {
        pickupRequestDateFrom: getDateAndTime().date,
        pickupRequestDateTo: getDateAndTime().date,
        pickupRequestNo: '',
        //client: '',
        pickupLocation: '',
        pickupStatus: ''
    };
    const [searchFilters, setSearchFilters] = useState(initialFilterState);

    // Table state
    const [loading, setLoading] = useState(false);
    const [currentPageNo, setCurrentPageNo] = useState(1);
    const [filterData, setFilterData] = useState(initializeFilterData);
    const [sortConfig, setSortConfig] = useState({ SortColumn: null, SortOrder: null });

    // ── Live Pickup Tracking State ──
    const [selectedPickupRequestNo, setSelectedPickupRequestNo] = useState('');
    const [trackingData, setTrackingData] = useState(null);
    const [trackingLoading, setTrackingLoading] = useState(false);




    // ── Create Pickup Request Form State (Blank by default) ──
    const initialFormData = {
        pickupRequestID: "",
        //client: "",
        pickupLocation: "",
        pickupDate: getDateAndTime().date,
        pickupSlotFromTime: "",
        pickupSlotToTime: "",
        consignorMobileNo: "",
        pieces: "",
        weight: "",
        materialDescription: "",
        vehicleType: "",
        specialInstruction: "",
        expectedDockets: "",
        pickupRequestNo: "System Generated"
    };
    const [formData, setFormData] = useState(initialFormData);

    const GetPickupPoint = DropDownData('GetPickupPoint', formData.client)
    const GetPickupPointP = DropDownData('GetPickupPoint', searchFilters.client)
    const GetPickupStatus = DropDownData('GetPickupStatus')

    const [result, setResult] = useState([]);
    // Auto-focus on opening Create form or List form
    useEffect(() => {
        if (viewMode === 'create') {
            const timer = setTimeout(() => {
                if (inputrefs.current['txtPickupLocation']) {
                    inputrefs.current['txtPickupLocation']?.focus();
                }
            }, 150);
            return () => clearTimeout(timer);
        } else if (viewMode === 'list') {
            const timer = setTimeout(() => {
                if (inputrefs.current['txtPickupRequestNo']) {
                    inputrefs.current['txtPickupRequestNo']?.focus();
                }
            }, 150);
            return () => clearTimeout(timer);
        }
    }, [viewMode]);

    // Handle Add New Click
    const handleAddNew = () => {
        setFormData(initialFormData);
        setViewMode('create');
        setTimeout(() => {
            if (inputrefs.current['txtPickupLocation']) {
                inputrefs.current['txtPickupLocation']?.focus();
            }
        }, 150);
    };
    const trackingRef = useRef(null);

    const handleClear = () => {
        setSearchFilters(initialFilterState);
        filterData.SearchText = '';
        setSortConfig({ SortColumn: null, SortOrder: null });
        setSelectedPickupRequestNo('');
        setTrackingData(null);
        inputrefs.current['txtPickupRequestNo']?.focus();
    };
    const handleSubmit = async () => {
        await dispatch(setSubLoading(true))
        let data = await ApiCall('/api/PickupRequest/InsertUpdatePickupRequest', formData)
        await dispatch(setSubLoading(false))
        await NotificationSound(data.status, data.message, data.focus, inputrefs, handleClearForm);
    };
    const fetchPickupTracking = async (requestNo) => {
        let reqNo = '';
        if (typeof requestNo === 'object' && requestNo !== null) {
            reqNo = requestNo?.PickupRequestNo || requestNo?.['Pickup Request No.'] || requestNo?.pickupRequestNo || '';
        } else if (typeof requestNo === 'string') {
            reqNo = requestNo.trim();
        }
        if (!reqNo) return;
        setSelectedPickupRequestNo(reqNo);
        setTrackingLoading(true);
        try {
            let data = await ApiCall('/api/PickupRequest/GetPickupRequestTracking', {
                PickupRequestNo: reqNo
            });
            if (data?.status === 'SUCCESS' || data?.Status === 'SUCCESS') {
                setTrackingData(data);
            } else {
                setTrackingData(null);
            }
        } catch (err) {
            setTrackingData(null);
        } finally {
            setTrackingLoading(false);
        }
    };

    // Auto-scroll down to tracking section when tracking data loads
    useEffect(() => {
        if (trackingData && trackingRef.current) {
            const timer = setTimeout(() => {
                trackingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 150);
            return () => clearTimeout(timer);
        }
    }, [trackingData]);

    const getAccountMasterData = async (CurrentPage) => {
        let PageIndex = 1
        if (CurrentPage !== undefined && typeof (CurrentPage) !== "object") {
            PageIndex = CurrentPage;
            setCurrentPageNo(CurrentPage);
        }
        else {
            setCurrentPageNo(1);
        }
        let GetData = {
            ...filterData,
            ...sortConfig,
            ...searchFilters,
            "PageIndex": PageIndex.toString(),
            "Type": "S",
        }
        setLoading(true)
        let data = await ApiCall('/api/PickupRequest/ShowPickupRequest', GetData)
        setResult(data)
        setLoading(false)
        if (data.Status === 'EXPIRED') {
            await NotificationSound(data.Status, data.Message, data.focus, inputrefs);
        }
    };
    const handleEdit = async (ID) => {
        await dispatch(setSubLoading(true));
        let data = await ApiCall('/api/PickupRequest/EditPickupRequest', {
            pickupRequestID: ID
        });
        await dispatch(setSubLoading(false));

        if (data.status === 'SUCCESS') {
            setFormData((pre) => ({
                ...pre,
                ...data.data
            }));
            setViewMode("create");
            await inputrefs.current[data?.focus]?.focus();
        } else {
            await NotificationSound(data.status, data.message, data.focus, inputrefs);
        }
    };
    const handleDelete = async (ID) => {
        await dispatch(setSubLoading(true))
        await deleteData('/api/PickupRequest/DeletePickupRequest', { pickupRequestID: ID }, getAccountMasterData, handleClear, '', inputrefs)
        await dispatch(setSubLoading(false))
    };

    const handleCancel = async (index) => {
        const result = await showConfirmDialog({
            title: "Are you sure?",
            text: "Do you want to cancel this record?",
            icon: "warning",
            requireRemark: true,
            remarkPlaceholder: "Enter cancellation remark...",
            confirmText: "Yes, Cancel Bill",
            cancelText: "Cancel"
        });

        if (result.isConfirmed) {
            const request = {
                pickupRequestID: index,
                remarks: result.value
            };

            try {
                await dispatch(setSubLoading(true));

                const res = await ApiCall('/api/PickupRequest/CancelPickupRequest', request);
                const status = res?.status || res?.Status;

                if (status === "SUCCESS") {
                    getAccountMasterData()
                    await NotificationSound(status, res?.message, res?.focus, inputrefs);
                } else {
                    await NotificationSound(status, res?.message, res?.focus, inputrefs);
                }
            } catch (error) {
                NotificationSound("ERROR", "Delete failed, try again");
            } finally {
                await dispatch(setSubLoading(false));
            }
        }
    };

    // Handle Clear Form
    const handleClearForm = () => {
        setFormData(initialFormData);
        setTimeout(() => {
            if (inputrefs.current['txtPickupLocation']) {
                inputrefs.current['txtPickupLocation']?.focus();
            }
        }, 150);
    };

    const handleChange1 = (event, value) => {
        setCurrentPageNo(value);
        getAccountMasterData(value, searchFilters);
    };

    const handleSorting = (SortColumn) => {
        let SortOrder = 'ASC';
        if (sortConfig && sortConfig.SortColumn === SortColumn && sortConfig.SortOrder === 'ASC') {
            SortOrder = 'DESC';
        }
        setSortConfig({ SortColumn, SortOrder });
    };


    useEffect(() => {
        if (viewMode === 'list') {
            getAccountMasterData()
        }
        document.title = "Pickup Requests";
    }, [viewMode, sortConfig]);

    return (
        <div className="w-full space-y-3">

            {viewMode === 'create' ? (
                /* ── Create Form Card ── */
                <div className="bg-white  border border-slate-200/90  rounded-xl p-4 sm:p-5 shadow-xs space-y-4">
                    {/* Header: Title + Back Button */}
                    <div className="flex items-center justify-between pb-3 border-b border-slate-100 ">
                        <div className="flex items-center gap-2">
                            <Boxes className="w-5 h-5 text-[var(--theme-color)]" />
                            <h2 className="text-base sm:text-lg font-bold text-slate-800  tracking-tight">
                                {("Create Pickup Request")}
                            </h2>
                        </div>
                        <button
                            type="button"
                            onClick={() => setViewMode('list')}
                            className="flex items-center gap-1.5 ripple-btn px-4 py-1.5 bg-slate-800 text-white rounded-lg font-bold shadow-xs uppercase tracking-wider text-[11px] hover:bg-slate-900 transition-all duration-200 cursor-pointer"
                        >
                            <ArrowLeft className="w-3.5 h-3.5" />
                            <span>{("BACK TO LIST")}</span>
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">

                        <InputBox
                            id="txtPickupRequestNo"
                            label="Pickup Request No"
                            maxLength="10"
                            placeholder="System Generated"
                            inputrefs={inputrefs}
                            disabled
                            value={formData.pickupRequestNo}
                            onChange={(e) => setFormData({ ...formData, pickupRequestNo: e.target.value })}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    inputrefs.current['txtPickupLocation']?.focus();
                                }
                            }}
                            divclassname="w-full"
                        />

                        {/* 2. Select Pickup Location from Warehouse */}
                        <SelectInputBox
                            id="txtPickupLocation"
                            label="Pickup Location"
                            required
                            value={formData.pickupLocation}
                            onChange={(e) => {
                                setFormData({
                                    ...formData,
                                    pickupLocation: e.target.value,
                                });
                            }}
                            inputrefs={inputrefs}
                            onKeyDown={(e) => e.key === 'Enter' && inputrefs.current['txtPickupDate'].focus()}
                        >
                            <option value="">{("Select Pickup Location")}</option>
                            {GetPickupPoint?.data?.dropDownData?.map((item, index) =>
                                <option key={item.textPart + index} value={item.valuePart}>{item.textPart}</option>
                            )}

                        </SelectInputBox>

                        {/* 3. Pickup Date */}
                        <DateBox
                            inputrefs={inputrefs}
                            label="Pickup Date"
                            placeholder="DD-MMM-YYYY"
                            id="txtPickupDate"
                            selected={
                                formData?.pickupDate
                                    ? new Date(formData.pickupDate)
                                    : null
                            }
                            onChange={(date) =>
                                handleDateChange(date, setFormData, formData, 'pickupDate')
                            }
                            required
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    inputrefs.current["txtPickupSlotFrom"]?.focus();
                                }
                            }}
                        />

                        <div>
                            <label className="block text-sm font-medium text-[#535353] mb-1">
                                Pickup Slot  <span className="!text-red-500">*</span>
                            </label>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <InputBox
                                    id="txtPickupSlotFromTime"
                                    type="time"
                                    placeholder="Pickup Slot From"
                                    value={formData.pickupSlotFromTime}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            pickupSlotFromTime: sanitizeInput(e.target.value)
                                        })
                                    }
                                    inputrefs={inputrefs}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            inputrefs.current["txtPickupSlotToTime"]?.focus();
                                        }
                                    }}
                                />

                                <InputBox
                                    id="txtPickupSlotToTime"
                                    type="time"
                                    placeholder="Pickup Slot To"
                                    value={formData.pickupSlotToTime}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            pickupSlotToTime: sanitizeInput(e.target.value)
                                        })
                                    }
                                    inputrefs={inputrefs}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            inputrefs.current["txtConsignorMobileNo"]?.focus();
                                        }
                                    }}
                                />
                            </div>
                        </div>

                        {/* 4. Pickup Slot Time Range */}


                        {/* 5. Consignor Mobile No */}
                        <InputBox
                            id="txtConsignorMobileNo"
                            label="Consignor Mobile No"
                            placeholder="Enter Consignor Mobile No"
                            required
                            maxLength="10"
                            inputrefs={inputrefs}
                            value={formData.consignorMobileNo || ''}
                            onChange={(e) => setFormData({ ...formData, consignorMobileNo: convertToNumber(e.target.value) })}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') inputrefs.current["txtPieces"].focus();
                            }}
                        />

                        {/* 6. Expected Pieces */}
                        <InputBox
                            id="txtPieces"
                            label="Expected Pieces"
                            placeholder="Enter Expected Pieces"
                            required
                            inputrefs={inputrefs}
                            value={formData.pieces || ''}
                            onChange={(e) => setFormData({ ...formData, pieces: convertToNumber(e.target.value) })}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') inputrefs.current["txtWeight"].focus();
                            }}
                        />

                        {/* 7. Expected Weight (Kg) */}
                        <InputBox
                            id="txtWeight"
                            label="Expected Weight (Kg)"
                            placeholder="Enter Expected Pieces"
                            required
                            inputrefs={inputrefs}
                            value={formData.weight || ''}
                            onChange={(e) => setFormData({ ...formData, weight: convertToNumber(e.target.value) })}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') inputrefs.current["txtExpectedDockets"].focus();
                            }}
                        />
                        <InputBox
                            id="txtExpectedDockets"
                            label="Expected Dockets"
                            placeholder="Enter Expected Dockets"
                            required
                            inputrefs={inputrefs}
                            value={formData.expectedDockets || ''}
                            onChange={(e) => setFormData({ ...formData, expectedDockets: convertToNumber(e.target.value) })}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') inputrefs.current["txtMaterial"].focus();
                            }}
                        />

                        {/* 8. Material Description */}
                        <InputBox
                            id="txtMaterial"
                            label="Material Description"
                            placeholder="e.g. Garments / Auto Parts"
                            inputrefs={inputrefs}
                            value={formData.materialDescription || ''}
                            onChange={(e) => setFormData({ ...formData, materialDescription: sanitizeInput(e.target.value) })}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') inputrefs.current["txtVehicleType"].focus();
                            }}
                        />

                        {/* 9. Required Vehicle Type */}
                        <SelectInputBox
                            id="txtVehicleType"
                            required
                            label="Vehicle Type"
                            inputrefs={inputrefs}
                            value={formData.vehicleType || ''}
                            onChange={(e) => setFormData({ ...formData, vehicleType: e.target.value })}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') inputrefs.current["txtSpecialInstruction"].focus();
                            }}
                        >

                            <option value=''> - Select - </option>
                            {GetVehicleType?.data?.dropDownData?.map((item, index) => (
                                <option key={item.valuePart + index} value={item.valuePart}>
                                    {(item.textPart)}
                                </option>
                            ))}
                        </SelectInputBox>

                        {/* 10. Special Instruction */}
                        <TextareaBox
                            id="txtSpecialInstruction"
                            label="Special Instruction"
                            rows={2}
                            maxLength="300"
                            divclassname="md:col-span-1 lg:col-span-2"
                            placeholder="Enter any special instructions..."
                            inputrefs={inputrefs}
                            value={formData.specialInstruction || ''}
                            onChange={(e) => setFormData({ ...formData, specialInstruction: sanitizeInput(e.target.value) })}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    inputrefs.current['btnSub']?.focus();
                                }
                            }}
                        />
                    </div>

                    {/* Standard FormButton for Submit / Clear */}
                    <div className="pt-2 border-t border-slate-100 ">
                        <FormButton
                            id={'btnSub'}
                            inputrefs={inputrefs}
                            handleSubmit={handleSubmit}
                            Export={false}
                            handleClear={handleClearForm}
                        />
                    </div>
                </div>
            ) : (
                <>
                    {/* ── UNIFIED SINGLE CARD (Header + Filters) ── */}
                    <div className="bg-white  border border-slate-200/90 rounded-xl p-3.5 sm:p-4 shadow-xs space-y-3">
                        {/* 1. Header Section */}
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                            {/* Left Side: Icon + Title */}
                            <div className="flex items-center gap-2">
                                <Boxes className="w-5 h-5 text-[var(--theme-color)]" />
                                <h2 className="text-base sm:text-lg font-bold text-slate-800  tracking-tight">
                                    {("Pickup Requests")}
                                </h2>
                            </div>

                            {/* Right Side: Action Buttons */}
                            <div className="flex items-center gap-2 self-end sm:self-auto">
                                <button
                                    type="button"
                                    onClick={handleAddNew}
                                    className="flex items-center gap-1.5 ripple-btn px-3.5 py-1.5 bg-[var(--theme-color)] text-white rounded-lg font-bold shadow-xs uppercase tracking-wider text-[11px] hover:opacity-90 transition-all duration-200 cursor-pointer"
                                >
                                    <Plus className="w-3.5 h-3.5" />
                                    <span>{("ADD NEW")}</span>
                                </button>
                            </div>
                        </div>

                        {/* 2. Filter Inputs Section - 4 per row */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3.5 items-end">
                            {/* 1. From Pickup Date */}
                            <DateBox
                                label="From Pickup Request Date"
                                placeholder="DD-MMM-YYYY"
                                id="txtFromPickupDate"
                                inputrefs={inputrefs}
                                selected={searchFilters.pickupRequestDateFrom ? new Date(searchFilters.pickupRequestDateFrom) : null}
                                onChange={(date) => handleDateChange(date, setSearchFilters, searchFilters, 'pickupRequestDateFrom')}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        inputrefs.current['txtFilterToPickupDate']?.focus();
                                    }
                                }}
                            />

                            {/* 2. To Pickup Date */}
                            <DateBox
                                label="To Pickup Request Date"
                                placeholder="DD-MMM-YYYY"
                                id="txtFilterToPickupDate"
                                inputrefs={inputrefs}
                                selected={searchFilters.pickupRequestDateTo ? new Date(searchFilters.pickupRequestDateTo) : null}
                                onChange={(date) => handleDateChange(date, setSearchFilters, searchFilters, 'pickupRequestDateTo')}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        inputrefs.current['txtPickupRequestNo']?.focus();
                                    }
                                }}
                            />

                            {/* 3. Pickup Request No */}
                            <InputBox
                                id="txtPickupRequestNo"
                                label="Pickup Request No"
                                placeholder="Enter Request No"
                                maxLength="10"
                                inputrefs={inputrefs}
                                value={searchFilters.pickupRequestNo}
                                onChange={(e) => setSearchFilters({ ...searchFilters, pickupRequestNo: sanitizeInput(e.target.value) })}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        inputrefs.current['txtPickupLocation']?.focus();
                                    }
                                }}
                                divclassname="w-full"
                            />

                            {/* 4. Client Autocomplete */}
                            {/*<AutoCompleteBoxNew*/}
                            {/*    id="txtFilterClient"*/}
                            {/*    label="Client"*/}
                            {/*    placeholder="Select Client"*/}
                            {/*    inputrefs={inputrefs}*/}
                            {/*    autocompleteType="GetClientMaster"*/}
                            {/*    value={searchFilters.client || ''}*/}
                            {/*    onChange={async (event, newInputValue) => {*/}
                            {/*        newInputValue = newInputValue || '';*/}
                            {/*        if (event.type !== 'keydown') {*/}
                            {/*            setSearchFilters({ ...searchFilters, client: newInputValue });*/}
                            {/*        }*/}
                            {/*    }}*/}
                            {/*    onKeyDown={(e) => {*/}
                            {/*        if (e.key === 'Enter') {*/}
                            {/*            e.preventDefault();*/}
                            {/*            inputrefs.current['txtPickupLocation']?.focus();*/}
                            {/*        }*/}
                            {/*    }}*/}
                            {/*    divclassname="w-full"*/}
                            {/*/>*/}

                            {/* 5. Location SelectBox */}
                            <SelectInputBox
                                id="txtPickupLocation"
                                label="Pickup Location"
                                value={searchFilters.pickupLocation}
                                onChange={(e) => setSearchFilters({ ...searchFilters, pickupLocation: e.target.value })}
                                inputrefs={inputrefs}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        inputrefs.current['txtStatus']?.focus();
                                    }
                                }}
                                divclassname="w-full"
                            >
                                <option value="">--Please Select--</option>
                                {GetPickupPointP?.data?.dropDownData?.map((item, index) =>
                                    <option key={item.textPart + index} value={item.valuePart}>{item.textPart}</option>
                                )}

                            </SelectInputBox>

                            {/* 6. Status SelectBox */}
                            <SelectInputBox
                                id="txtStatus"
                                label="Status"
                                value={searchFilters.pickupStatus}
                                onChange={(e) => setSearchFilters({ ...searchFilters, pickupStatus: e.target.value })}
                                inputrefs={inputrefs}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        inputrefs.current['btnFilter']?.focus();
                                    }
                                }}
                                divclassname="w-full"
                            >
                                <option value="">--Please Select--</option>
                                {GetPickupStatus?.data?.dropDownData?.map((item, index) =>
                                    <option key={item.textPart + index} value={item.valuePart}>{item.textPart}</option>
                                )}

                            </SelectInputBox>
                        </div>

                        {/* 3. Search, Reset & Export Buttons */}
                        <div className="flex items-center justify-start gap-2 pt-1">
                            <FormButton
                                id="btnFilter"
                                inputrefs={inputrefs}
                                search={true}
                                Reset={true}
                                save={false}
                                Export={true}
                                extraclassname=""
                                handleClear={handleClear}
                                handleSearch={getAccountMasterData}

                                handleExport={async () => {
                                    await dispatch(setSubLoading(true))
                                    await handleDownloadExcel(`/api/PickupRequest/ShowPickupRequest`, { ...filterData, ...searchFilters }, 'Pickup Request')
                                    await dispatch(setSubLoading(false))
                                }}
                            />

                        </div>
                    </div>

                    {/* ── Table Card ── */}
                    <div className='mt-3'>
                        <Table
                            handleEdit={handleEdit}
                            handleDelete={handleDelete}
                            PageName={'PickupRequests'}
                            loading={loading}
                            handleFilter={() => {
                                getAccountMasterData(1, searchFilters)
                                setFilterData({
                                    ...filterData,
                                })
                            }}
                            Filter={false}
                            onlyFilterPage={true}
                            filterData={filterData}
                            tableData={result}
                            setFilterData={setFilterData}
                            currentPageNo={currentPageNo}
                            handleChange1={handleChange1}
                            handleSorting={handleSorting}
                            sortConfig={sortConfig}
                            handleCancel={handleCancel}
                            matchingkey="PickupRequestNo"
                            ClickOnLink={(val, row) => fetchPickupTracking(val || row)}
                        />
                    </div>

                    {/* ── Live Pickup Tracking Card (Niche Tracking Show) ── */}
                    {(trackingData || trackingLoading || selectedPickupRequestNo) && (
                        <div ref={trackingRef} className='mt-1.5'>
                            <PickupTrackingCard
                                pickupRequestNo={selectedPickupRequestNo}
                                trackingData={trackingData}
                                loading={trackingLoading}
                                onRefresh={() => selectedPickupRequestNo && fetchPickupTracking(selectedPickupRequestNo)}
                                onClose={() => {
                                    setTrackingData(null);
                                    setSelectedPickupRequestNo('');
                                }}
                            />
                        </div>
                    )}
                </>
            )}

        </div>
    );
};

export default PickupRequests;
